<script>
  import breakpoints from '@/mixins/breakpoints';
  import { CURRENCIES } from '@/store';

  import IconDollar from '@/assets/images/svg/icon-dollar.svg';
  import IconQcCoin from '@/assets/images/svg/icon-qc-coin.svg';
  import IconBell from '@/assets/images/svg/icons/bell-solid.svg';
  import TimedTooltip from '@/components/popups/TimedTooltip.vue';
  import YellowButton from '@/components/base/YellowButton.vue';

  export default {
    name: 'TopNav',

    components: {
      YellowButton,
      TimedTooltip,
      IconBell,
      IconDollar,
      IconQcCoin,
    },

    emits: ['sidebar-toggle', 'notification-toggle'],

    mixins: [breakpoints],

    props: {
      isLoggedInUser: {
        type: Boolean,
        default: false,
      },

      notificationDrawer: {
        type: Boolean,
        required: true,
      },

      showAlerts: {
        type: Boolean,
        required: true,
      },
    },

    data: () => ({
      chatIsAvailable: false,
    }),

    computed: {
      isSidebarOpened() {
        return this.$store.state.sidebar;
      },

      currencies() {
        return [
          {
            type: CURRENCIES.MMC,
            balance: this.balance.mmc,
            color: 'white',
            icon: IconDollar,
          },
          {
            type: CURRENCIES.MQC,
            balance: this.balance.mqc,
            color: 'gold',
            icon: IconQcCoin,
          },
        ];
      },
      playerMode() {
        return this.$store.state.playermode;
      },

      selectedCurrency() {
        return {
          icon: this.playerMode === CURRENCIES.MMC ? 'IconDollar' : 'IconQcCoin',
          balance: this.balance[this.playerMode.toLowerCase()],
          color: this.playerMode === CURRENCIES.MMC ? 'white' : 'gold',
        };
      },

      balance() {
        return this.$store.getters.getFormattedBalance;
      },

      isUserSuspended() {
        return this.$store.getters.isUserSuspended;
      },

      totalNotificationAmount() {
        return this.$store.state.totalNotificationAmount || 0;
      },
    },

    mounted() {
      this.chatIsAvailable = typeof $zoho !== 'undefined';
    },

    methods: {
      setSelected(currencyType) {
        this.$store.commit('setPlayerMode', currencyType);
      },

      openWalletDialog() {
        this.$store.dispatch('setWalletDialog', {
          value: true,
          tab: 'purchase',
        });
      },

      // openPromos() {
      //   this.$router.push('/playerrewards');
      // },

      openLogInDialog() {
        this.$store.dispatch('changeLoginDialogState', true);
      },

      openSignUpDialog() {
        this.$store.dispatch('changeSignupDialogState', true);
      },
    },
  };
</script>

<template>
  <div
    id="top-nav"
    :class="{
      'top-nav--logged-out': !isLoggedInUser,
      'top-nav--logged-in': isLoggedInUser,
    }"
    class="top-nav"
  >
    <div class="left-side">
      <v-app-bar-nav-icon
        v-if="isLoggedInUser && isMobileOrTablet"
        id="menu-toggle"
        class="menu-toggle"
        @click="$emit('sidebar-toggle')"
      >
        <v-app-bar-nav-icon :ripple="false" />
        <span v-if="totalNotificationAmount" class="badge">
          {{ totalNotificationAmount }}
        </span>
      </v-app-bar-nav-icon>
    </div>
    <div v-if="isLoggedInUser" :class="{ 'sidebar-opened': isSidebarOpened }" class="right-side">
      <v-menu content-class="top-nav-dropdown" offset-y>
        <template v-slot:activator="{ on }">
          <v-btn class="selected-currency" height="48" v-on="on">
            <component :is="selectedCurrency.icon" class="currency-icon" />
            <span :class="`${selectedCurrency.color}--text`" class="currency-amount">
              {{ selectedCurrency.balance }}
            </span>
            <v-icon :size="isMobileOrTablet ? 'small' : 'default'">mdi-chevron-down</v-icon>
          </v-btn>
          <YellowButton @click="openWalletDialog" class="wallet-button">Deposit</YellowButton>
        </template>
        <v-list>
          <v-list-item v-for="currency in currencies" :key="currency.type" @click="setSelected(currency.type)">
            <div class="user-balance">
              <component :is="currency.icon" class="currency-icon" />
              <span :class="`${currency.color}--text`">
                {{ currency.balance }}
              </span>
            </div>
          </v-list-item>
        </v-list>
      </v-menu>

      <!--
      <v-btn
        v-if="!isMobileOrTablet"
        :disabled="isUserSuspended"
        class="promo-button"
        data-test-id="promos"
        @click="openPromos"
      >
        Promos
      </v-btn>
      -->

      <!-- Duplicate of item above for mobiles -->
      <timed-tooltip :hidden="notificationDrawer" class="notification-icon">
        <div class="badge-container" data-test-id="notification-ring-button" @click="$emit('notification-toggle')">
          <v-badge :class="showAlerts ? 'red-dot' : 'no-dot'" bordered class="header-badge" color="red" overlap>
            <v-avatar size="18">
              <IconBell />
            </v-avatar>
          </v-badge>
        </div>
      </timed-tooltip>
    </div>
    <div v-else class="auth-buttons">
      <v-btn data-test-id="log-in-button" large @click="openLogInDialog">Login</v-btn>
      <v-btn class="sign-up" data-test-id="sign-up-button" large @click="openSignUpDialog">Sign Up</v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import 'src/scss/mixins';

  @keyframes notification-twitching {
    0% {
      transform: scale(1);
    }
    19% {
      transform: scale(1.2) rotate(0);
    }
    20% {
      transform: scale(1.2) rotate(10deg);
    }
    25% {
      transform: scale(1.2) rotate(-10deg);
    }
    30% {
      transform: scale(1.2) rotate(10deg);
    }
    35% {
      transform: scale(1.2) rotate(-10deg);
    }
    40% {
      transform: scale(1.2) rotate(0);
    }
    45% {
      transform: scale(1) rotate(0);
    }
    100% {
      transform: scale(1) rotate(0);
    }
  }

  .game-experience {
    font-size: 19px;
    display: none;

    @media screen and (min-width: 1024px) {
      display: block;
      transform: translateX(100px);
    }
  }

  .top-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    margin-inline: auto;

    @media screen and (min-width: 990px) {
      width: 100%;
    }

    .left-side {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      cursor: pointer;

      @media screen and (min-width: 420px) {
        gap: 24px;
      }
    }

    .v-app-bar__nav-icon {
      background: #31114b;
      border-radius: 6px;
      width: 48px;
      height: 48px;
    }

    .logo-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      .mq-logo {
        width: 32vw;
        max-width: 100px;

        @media screen and (min-width: 380px) {
          max-width: 165px;
        }

        @media screen and (min-width: 1280px) {
          max-width: 200px;
        }
      }

      .icon-beta {
        position: absolute;
        right: -20px;
        width: 6vw;
        max-width: 23px;

        @media screen and (min-width: 420px) {
          width: 5vw;
        }

        @media screen and (min-width: 1024px) {
          max-width: 40px;
          right: -40px;
        }
      }
    }

    .auth-buttons {
      display: flex;
      gap: 8px;

      .v-btn {
        height: 44px;
        background: rgba(255, 255, 255, 0.07);
        box-shadow: 0 0 0 1px inset rgba(255, 255, 255, 0.3);
        border: 0;
        border-radius: 6px;
        padding: 14px 18px;

        ::v-deep .v-btn__content {
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-weight: 700;
          text-transform: none;
          line-height: 100%;
          color: #fff;
        }

        &.sign-up {
          background: #f8bf1e;
          border: 0;
          padding: 14px 20px;
          box-shadow: none;

          ::v-deep .v-btn__content {
            color: #140324;
          }
        }

        @media screen and (min-width: 1024px) {
          padding: 16px 32px !important;
        }
      }
    }

    .right-side {
      transition: 0.35s ease;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      @media screen and (min-width: 600px) {
        gap: 10px;
      }

      @media screen and (min-width: 1024px) {
        min-width: 40%;
        justify-content: flex-end;
      }
    }

    .notification-icon {
      cursor: pointer;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.1);

      @media screen and (max-width: 600px) {
        display: none;
      }

      @media screen and (min-width: 990px) {
        transform: scale(1);
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .badge-container {
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #361550;
      border-radius: 8px;

      .red-dot {
        animation: notification-twitching 4s infinite;
      }
    }
  }

  @container app-bar (min-width: 0px) {
    .top-nav {
      width: calc(100% - 32px);
    }
  }
  @container app-bar (min-width: 480px) {
    .top-nav {
      width: calc(100% - 48px);
    }
  }
  @container app-bar (min-width: 768px) {
    .top-nav {
      width: calc(100% - 64px);
    }
  }
  @container app-bar (min-width: 1024px) {
    .top-nav {
      width: calc(100% - 96px);

      &.top-nav--logged-in {
        @media screen and (min-width: 1281px) {
          width: 100%;
          max-width: calc(100vw - 389px - 64px);
        }

        @media screen and (min-width: 1842px) {
          max-width: 1448px;
          padding-inline: 32px;
        }
      }

      &.top-nav--logged-out {
        @media screen and (min-width: 1600px) {
          width: 100%;
          max-width: 1448px;
        }
      }
    }
  }

  .right-side .promo-button {
    height: 48px;
    font-size: 16px;
    font-weight: 700;
    padding: 16px 32px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.07);

    &::v-deep .v-btn__content {
      color: #fff;
      text-align: center;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 100%;
      text-transform: none;
    }
  }

  .menu-toggle .badge {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 16px;
    height: 16px;
    background: #dc0000;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 900;
  }

  .selected-currency.v-btn {
    display: flex;
    height: 48px;
    padding: 16px 10px;
    align-items: center;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    background: rgba(0, 0, 0, 0.3);

    .currency-amount {
      text-align: center;
      font-family: 'Barlow', sans-serif;
      font-size: 18px;
      font-weight: 500;
      line-height: 100%;
    }

    svg {
      width: 30px;
      flex-grow: 0;
    }

    .v-icon {
      color: #fff;
      opacity: 0.4;
    }

    ::v-deep .v-btn__content {
      gap: 20px;
    }

    ::v-deep .v-ripple__container {
      display: none;
    }
  }

  .wallet-button {
    position: relative;
    height: 48px;
    margin-left: 8px;
    font-family: 'Inter', sans-serif !important;
    font-size: 16px;
    font-weight: 700;
    padding: 16px 32px;
    border-radius: 6px;

    @media screen and (min-width: 600px) {
      margin-left: -16px;
    }

    @media screen and (min-width: 616px) {
      margin-left: 0;
    }
  }
</style>

<style lang="scss">
  .top-nav-dropdown {
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.07);
    overflow: hidden;
    transform: translateY(5px);

    .v-list {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      background: #18062a;
      padding: 12px 6px;
      gap: 16px;
    }

    .v-list-item {
      min-height: 26px;
      padding: 0;
    }

    .user-balance {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      svg {
        flex-grow: 0;
        margin-left: 8px;
      }

      span {
        color: #f8bf1e;
        font-size: 18px;
        font-weight: 500;
        margin-right: 8px;
      }

      &--mqc {
        span {
          color: #4bbdff;
        }
      }
    }

    .coins-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 10px;
      border-radius: 5px;
      text-transform: none;
      background: var(--v-gold-base) !important;

      &.v-btn--disabled {
        background: var(--v-gold-base) !important;
        filter: grayscale(1);
        pointer-events: none;
      }

      svg {
        max-width: 18px;

        path {
          fill: #1c0630;
        }
      }

      .v-btn__content {
        align-items: normal;
      }

      .coins-text {
        color: #1c0630;
        font-size: 16px;
        font-weight: 700;
        transform: translateY(-1px);
      }

      @media screen and (min-width: 1024px) {
        justify-content: center;
        gap: 12px;
      }
    }

    .currency-icon {
      width: 22px;
      height: 22px;
    }
  }
</style>
